import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Palette, Star, Trash2, Save, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { applyFace } from "@/components/editor/FacePanel";

const KEYS = ["aspect", "fit", "quality", "platform", "effects", "subs", "sub_style", "sub_pos", "sub_x", "sub_y", "sub_scale", "pro", "music", "zoom", "split_ratio", "cam_zoom", "hq"];

export const cfgToTpl = (cfg) => ({ ...Object.fromEntries(KEYS.map((k) => [k, cfg[k] ?? null]).filter(([k, v]) => v !== null || ["pro", "music"].includes(k))), auto_face: cfg.auto_face !== false });

export const tplToCfg = (c, st, facecam) => {
  const n = { ...c, ...Object.fromEntries(KEYS.filter((k) => k in st).map((k) => [k, st[k]])) };
  if (n.fit === "split" && n.aspect !== "9:16") n.fit = "blur";
  return st.auto_face !== false && facecam?.found ? applyFace(n, facecam) : { ...n, auto_face: false };
};

export const TemplatesPanel = ({ video, cfg, setCfg }) => {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/templates").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const apply = (tp) => { setCfg((c) => tplToCfg(c, tp.settings, video.facecam)); toast.success(t("tpl.applied", { n: tp.name })); };
  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.post("/templates", { name: name.trim(), settings: cfgToTpl(cfg) }); setName(""); toast.success(t("tpl.saved")); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const star = async (tp) => { await api.patch(`/templates/${tp.id}`, { is_default: !tp.is_default }).catch((e) => toast.error(formatApiError(e))); load(); };
  const del = async (tp) => { await api.delete(`/templates/${tp.id}`).catch(() => {}); toast(t("tpl.deleted")); load(); };
  return (
    <div className="cx-card rounded-xl p-4 sm:p-5 space-y-3" data-testid="templates-panel">
      <div className="flex items-center gap-2 text-white font-semibold"><Palette className="w-4 h-4 text-[#3ad7ff]" />{t("tpl.title")}</div>
      <p className="text-[11px] text-slate-400">{t("tpl.desc")}</p>
      {list.length === 0 ? <p className="text-[11px] text-slate-500 italic" data-testid="templates-empty">{t("tpl.empty")}</p> : (
        <div className="space-y-1.5" data-testid="templates-list">
          {list.map((tp) => (
            <div key={tp.id} className="flex items-center gap-1.5 group" data-testid={`template-row-${tp.id}`}>
              <button onClick={() => apply(tp)} data-testid={`template-apply-${tp.id}`} className={`flex-1 min-w-0 text-left truncate rounded-lg px-3 py-2 text-xs border transition-colors ${tp.is_default ? "border-[#ffcc4d]/50 bg-[#ffcc4d]/[0.06] text-white" : "border-white/10 text-slate-300 hover:border-[#3ad7ff]/50 hover:text-white"}`}>
                {tp.name}<span className="ml-2 text-[10px] text-slate-500">{tp.settings.aspect} · {tp.settings.sub_style}{tp.settings.pro ? " · Pro" : ""}</span>
              </button>
              <button onClick={() => star(tp)} title={t(tp.is_default ? "tpl.unset_default" : "tpl.set_default")} data-testid={`template-default-${tp.id}`} className="p-2 rounded-lg hover:bg-white/5">
                <Star className={`w-4 h-4 ${tp.is_default ? "fill-[#ffcc4d] text-[#ffcc4d]" : "text-slate-500"}`} />
              </button>
              <button onClick={() => del(tp)} data-testid={`template-delete-${tp.id}`} className="p-2 rounded-lg text-slate-500 hover:text-[#ff4d6d] hover:bg-white/5"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input data-testid="template-name-input" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} placeholder={t("tpl.name")}
          className="flex-1 min-w-0 rounded-full bg-[#070912] border border-white/10 px-3 py-2 text-xs outline-none focus:border-[#3ad7ff]" />
        <button onClick={save} disabled={busy || !name.trim()} data-testid="template-save-btn" className="cx-btn rounded-full px-3 py-2 text-xs font-semibold flex items-center gap-1.5 shrink-0">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{t("tpl.save")}
        </button>
      </div>
    </div>
  );
};
